import type { Control, FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import type { CreateWorkOrderData, WorkOrder } from '../../../types/workOrder';
import type { BasicInfoFormData } from '../schemas/workOrderSchema';
import type { Machine } from '../../../types/machine';
import { AutocompleteSelect, type AutocompleteOption } from '../../../components/common/AutocompleteSelect';
import { openNativeDatePickerSafely, preventManualDateEntry, preventManualDatePasteOrDrop } from '../../../utils/date';
import { useLanguage } from '../../../features/language/useLanguage';

export const BasicInfoSection = ({
  register,
  control,
  setValue,
  errors,
  initialData,
  machineOptions,
  roasterOptions,
  onCreateMachine,
  onCreateRoaster,
  canCreateMachine = true,
  canCreateRoaster = true,
  selectedMachine,
  isCompleted,
  isAtMachineTypeLimit,
}: {
  register: UseFormRegister<BasicInfoFormData>;
  control: Control<BasicInfoFormData>;
  setValue: UseFormSetValue<BasicInfoFormData>;
  errors: FieldErrors<BasicInfoFormData>;
  initialData?: Partial<CreateWorkOrderData> | Partial<WorkOrder>;
  machineOptions: AutocompleteOption[];
  roasterOptions: AutocompleteOption[];
  onCreateMachine: (text: string) => void;
  onCreateRoaster: (text: string) => void;
  canCreateMachine?: boolean;
  canCreateRoaster?: boolean;
  selectedMachine?: Machine;
  isCompleted?: boolean;
  isAtMachineTypeLimit?: boolean;
}) => {
  const { t } = useLanguage();
  const workOrderNumber = (initialData as Partial<WorkOrder> | undefined)?.workOrderNumber;

  return (
    <div className="border-b pb-6">
      <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">{t('inventory.basicInfo.title')}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('workOrders.form.workOrderCode')}</label>
            <input
              disabled
              value={workOrderNumber || ''}
              placeholder={t('inventory.basicInfo.generatedBySystem')}
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm sm:text-sm border p-2"
              data-test-id="wo-code-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('roast.formHeader.machine')} *</label>
            <Controller
              control={control}
              name="machine"
              render={({ field }) => (
                <AutocompleteSelect
                  dataTestId="wo-machine-select"
                  value={field.value}
                  onChange={(text, option) => {
                    field.onChange(text);
                    const selectedId = option?.id || machineOptions.find((o) => o.text === text)?.id || '';
                    setValue('machineTypeId', selectedId, { shouldValidate: true });
                  }}
                  onCreateOption={onCreateMachine}
                  allowCustom={canCreateMachine && !isAtMachineTypeLimit}
                  options={machineOptions}
                  placeholder={t('workOrders.form.selectOrTypeMachine')}
                  error={errors.machineTypeId?.message || errors.machine?.message}
                  disabled={isCompleted}
                />
              )}
            />
            {selectedMachine && (
              <div className="mt-1 text-xs text-gray-500 flex flex-wrap gap-4">
                {selectedMachine.minimumInputKg !== undefined && (
                  <span>{t('workOrders.form.minInput')}: {selectedMachine.minimumInputKg}kg</span>
                )}
                {selectedMachine.maximumInputKg !== undefined && (
                  <span>{t('workOrders.form.maxInput')}: {selectedMachine.maximumInputKg}kg</span>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('roast.formHeader.roaster')} *</label>
            <Controller
              control={control}
              name="roaster"
              render={({ field }) => (
                <AutocompleteSelect
                  dataTestId="wo-roaster-select"
                  value={field.value}
                  onChange={(text, option) => {
                    field.onChange(text);
                    const selectedId = option?.id || roasterOptions.find((o) => o.text === text)?.id || '';
                    setValue('roasterId', selectedId);
                  }}
                  onCreateOption={onCreateRoaster}
                  allowCustom={canCreateRoaster}
                  options={roasterOptions}
                  placeholder={t('workOrders.form.selectOrTypeRoaster')}
                  error={errors.roaster?.message}
                  disabled={isCompleted}
                />
              )}
            />
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('reports.general.table.date')} *</label>
            <input
              type="date"
              {...register('targetDate')}
              disabled={isCompleted}
              onClick={openNativeDatePickerSafely}
              onKeyDown={preventManualDateEntry}
              onPaste={preventManualDatePasteOrDrop}
              onDrop={preventManualDatePasteOrDrop}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-100"
              data-test-id="wo-date-input"
            />
            {errors.targetDate && <p className="text-red-600 text-xs mt-1">{errors.targetDate.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('reports.shift.time')} *</label>
            <input
              type="time"
              {...register('time')}
              disabled={isCompleted}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-100"
              data-test-id="wo-time-input"
            />
            {errors.time && <p className="text-red-600 text-xs mt-1">{errors.time.message}</p>}
          </div>
        </div>
      </div>
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700">{t('forms.common.internalNotes')}</label>
        <textarea
          {...register('internalNotes')}
          disabled={isCompleted}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 h-24 disabled:bg-gray-100"
          data-test-id="wo-internal-notes-textarea"
        />
      </div>
    </div>
  );
};
